import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import Seo, { Breadcrumbs } from "@/components/Seo";
import { Phone, Mail, MapPin } from "lucide-react";

export default function Contact() {
  const [f, setF] = useState({ name: "", email: "", phone: "", message: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", f);
      toast.success("Message sent — we'll be in touch soon.");
      setF({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="container-bl py-10" data-testid="page-contact">
      <Seo title="Contact B&L Rentals in Oxnard · Vehicle Rental Support" description="Reach B&L Rentals in Oxnard, CA. Phone, email, and contact form for booking questions." canonical={typeof window !== "undefined" ? window.location.origin + "/contact" : undefined} />
      <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Contact" }]} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        <div>
          <div className="text-sm uppercase tracking-widest text-[#E2AB20] font-semibold">Get in touch</div>
          <h1 className="mt-2 text-4xl font-heading font-bold text-white">Contact our team</h1>
          <p className="mt-3 text-[#AEB9C9] max-w-lg">Questions about a booking, insurance, or delivery? Call, email, or send a note — we'll be in touch soon.</p>

          <ul className="mt-8 space-y-4 text-sm">
            <li className="flex items-start gap-3"><Phone size={18} className="text-[#E2AB20] mt-0.5" /> <div><div className="font-medium text-white"><a href="tel:+18052908431" className="hover:text-[#E2AB20]">(805) 290-8431</a></div></div></li>
            <li className="flex items-start gap-3"><Mail size={18} className="text-[#E2AB20] mt-0.5" /> <div><div className="font-medium text-white">hello@blrental.com</div><div className="text-[#AEB9C9]">General inquiries and quotes</div></div></li>
            <li className="flex items-start gap-3"><MapPin size={18} className="text-[#E2AB20] mt-0.5" /> <div><div className="font-medium text-white">Oxnard, CA 93030</div><div className="text-[#AEB9C9]">Exact pickup address shared with each confirmed booking</div></div></li>
          </ul>

          <div className="mt-8 rounded-xl overflow-hidden border border-gray-200 aspect-[4/3] bg-gray-100">
            <iframe
              title="Oxnard, CA map"
              src="https://www.google.com/maps?q=Oxnard%2C%20CA%2093030&z=12&output=embed"
              className="w-full h-full"
              loading="lazy"
              data-testid="contact-map"
            />
          </div>
        </div>

        <form onSubmit={submit} className="card-bl p-6 md:p-8 h-fit" data-testid="contact-form">
          <h2 className="text-xl font-heading font-semibold text-white">Send a message</h2>
          <div className="mt-5 space-y-4">
            <div><Label>Your name</Label><Input required value={f.name} onChange={(e) => setF({...f, name: e.target.value})} data-testid="contact-name" /></div>
            <div><Label>Email</Label><Input type="email" required value={f.email} onChange={(e) => setF({...f, email: e.target.value})} data-testid="contact-email" /></div>
            <div><Label>Phone (optional)</Label><Input value={f.phone} onChange={(e) => setF({...f, phone: e.target.value})} data-testid="contact-phone" /></div>
            <div><Label>Message</Label><Textarea required rows={5} value={f.message} onChange={(e) => setF({...f, message: e.target.value})} data-testid="contact-message" /></div>
            <Button type="submit" disabled={busy} className="bg-[#E2AB20] hover:bg-[#D89B08] w-full h-11" data-testid="contact-submit">
              {busy ? "Sending…" : "Send message"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
